import { useEffect, useState } from "react";

/** A keyboard takes at least this much of the screen. The browser's own bars coming and going take less. */
const KEYBOARD_PX = 150;

function typing(): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement) return true;
  return el instanceof HTMLInputElement && ["text", "search", "email", "tel", "url", "number", "password"].includes(el.type);
}

/**
 * True while an on-screen keyboard is really up: a text field has the focus and the part of the
 * page that can be seen (window.visualViewport) is a keyboard's height shorter than the page's
 * own viewport. Focus alone is not enough: on Android, Back closes the keyboard and leaves the
 * field focused. A browser without visualViewport never says true.
 */
export function useKeyboardUp(): boolean {
  const [up, setUp] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    // The page's viewport with no keyboard. Some browsers shrink it too when one comes up, so it
    // is only read while nobody is typing.
    let full = window.innerHeight;
    let width = window.innerWidth;
    const look = (): void => {
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        full = window.innerHeight;
      }
      if (!typing()) {
        full = window.innerHeight;
        setUp(false);
        return;
      }
      setUp(Math.max(full, window.innerHeight) - vv.height * vv.scale >= KEYBOARD_PX);
    };
    // A tap on Send takes the focus off the field before it lands. Looking a moment later keeps
    // whatever the keyboard was hiding from appearing under that finger.
    let later: ReturnType<typeof setTimeout> | undefined;
    const left = (): void => {
      clearTimeout(later);
      later = setTimeout(look, 300);
    };
    vv.addEventListener("resize", look);
    window.addEventListener("resize", look);
    document.addEventListener("focusin", look);
    document.addEventListener("focusout", left);
    look();
    return () => {
      clearTimeout(later);
      vv.removeEventListener("resize", look);
      window.removeEventListener("resize", look);
      document.removeEventListener("focusin", look);
      document.removeEventListener("focusout", left);
    };
  }, []);
  return up;
}
